import { useState, useEffect, ReactNode, type JSX } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { usePlacesWidget } from "react-google-autocomplete";
import { useSelector } from "react-redux";
import { cloneDeep } from "lodash";
import { Box, Button } from "@mui/material";
import EditLocationAltOutlinedIcon from "@mui/icons-material/EditLocationAltOutlined";
import { Control, Controller, FieldErrors, Path } from "react-hook-form";
import { AppAddressCountries } from "../../../../../utils/appEnums/AppEnums";
import { RootState } from "../../../../../store";
import { defaultPlaceAddress, GoogleAddressComponent, Place, PlaceAddress } from "../../../../../utils/appInterfaces/AppInterfaces";
import { StyledTextField, classes } from "./GoogleAutocompleteAddressStyles";
import { Address } from "./GoogleAutocompleteAddressTypes";
import { AddressErrorIdPrefix } from "../../../addressScreens";
import { TooltipWrapper } from "../../../../TooltipWrapper/TooltipWrapper";
import { useHideGoogleAddressOptionsOnScroll } from "../../../../../Hooks/useHideGoogleAddressOptionOnScroll";
import { fieldVariant } from "../../../../../utils/AppUtils";
import { FieldLabelWithCompletion } from "../../../../Common/FieldLabelWithCompletion/FieldLabelWithCompletion";
import ManualAddressOverlay, { TManualAddressOverlayVariant } from "../../../ManualAddressFields/ManualAddressOverlay";
import { IManualAddressFormValues, defaultManualAddressValues } from "../../../ManualAddressFields/ManualAddressFields";
import { composeFullAddress, locationToManualAddressValues, manualAddressToPlaceAddress } from "../../../ManualAddressFields/manualAddressAdapters";

interface IProps<T extends Address> {
    country: string | null;
    control: Control<T>;
    errors: FieldErrors<T>;
    getValues: (payload?: string | string[]) => any;
    setAddressValuesFromPlacesApi: (placeAddress: PlaceAddress, index?: number) => void;
    onAddressChange: () => void;
    errorIdPrefix: AddressErrorIdPrefix;
    index?: number;
    fieldLabel?: string;
    required?: boolean;
    requiredToComplete?: boolean;
    tooltipText?: string | ReactNode;
    /**
     * NG-7511: overlay style for the manual-entry fallback. Defaults to "flyin"; set "modal" whenever
     * the host is itself an overlay (a modal/Dialog OR a fly-in) — a fly-in cannot render usably on top
     * of another overlay. Only page-level hosts should keep the fly-in default.
     */
    overlayVariant?: TManualAddressOverlayVariant;
    /** NG-7511: hide the always-on manual toggle (the fallback still appears when Google is unavailable). */
    allowManualToggle?: boolean;
}

const GoogleAutocompleteAddress = <T extends Address>(props: IProps<T>): JSX.Element => {
    const {
        country,
        control,
        errors,
        getValues,
        setAddressValuesFromPlacesApi,
        onAddressChange,
        errorIdPrefix,
        index,
        fieldLabel,
        required,
        requiredToComplete,
        tooltipText,
        overlayVariant = "flyin",
        allowManualToggle = false,
    } = props;
    const [place, setPlace] = useState<Place | null>(null);
    const [countryCode, setCountryCode] = useState<string | null>("us");
    const [manualOverlayOpen, setManualOverlayOpen] = useState(false);
    // Remember the last manually-entered values so reopening "Edit address" preserves fields (County,
    // Address Line 2) that the composed one-line address can't round-trip on hosts without broken-out fields.
    const [manualValues, setManualValues] = useState<Partial<IManualAddressFormValues>>({});
    const googlePlacesApiKey: string = useSelector((store: RootState) => store.uiSliceReducer.googlePlacesApiKey);
    const googleMapsUnavailable = useSelector((store: RootState) => store.googleMapsStatusReducer.status === "unavailable");
    const { formatMessage } = useIntl();

    const { ref: placeRef }: any = usePlacesWidget({
        language: "en",
        apiKey: googlePlacesApiKey,
        onPlaceSelected: (newPlace) => {
            setPlace(newPlace);
        },
        options: {
            types: ["address"],
            componentRestrictions: { country: countryCode },
        },
    });
    const handleFocus = useHideGoogleAddressOptionsOnScroll(placeRef);

    const handlePlacesApiResponse = (response: Place): void => {
        if (response?.address_components) {
            const placeAddress = cloneDeep(defaultPlaceAddress);
            placeAddress.lat = response.geometry.location.lat();
            placeAddress.lon = response.geometry.location.lng();
            placeAddress.formatted_address = response.formatted_address;
            response.address_components.forEach((item: GoogleAddressComponent) => {
                if (item.types.includes("administrative_area_level_1")) {
                    placeAddress.administrative_area_level_1 = item;
                }
                if (item.types.includes("administrative_area_level_2")) {
                    placeAddress.administrative_area_level_2 = item;
                }
                if (item.types.includes("locality")) {
                    placeAddress.locality = item;
                }
                if (item.types.includes("postal_code")) {
                    placeAddress.postal_code = item;
                }
                if (item.types.includes("country")) {
                    placeAddress.country = item;
                }
                if (item.types.includes("route")) {
                    placeAddress.route = item;
                }
                if (item.types.includes("street_number")) {
                    placeAddress.street_number = item;
                }
            });
            if (index !== undefined) {
                setAddressValuesFromPlacesApi(placeAddress, index);
            } else {
                setAddressValuesFromPlacesApi(placeAddress);
            }
        }
    };

    useEffect(() => {
        if (place) {
            handlePlacesApiResponse(place);
        }
    }, [place]);

    useEffect(() => {
        switch (country) {
            case AppAddressCountries.CANADA:
                setCountryCode("ca");
                break;
            case AppAddressCountries.UNITED_STATES_OF_AMERICA:
                setCountryCode("us");
                break;
            case AppAddressCountries.AUSTRALIA:
                setCountryCode("au");
                break;
            default:
                setCountryCode("us");
        }
    }, [country]);

    const showClasses = (): string => {
        return getValues("address") !== "" ? classes.filledRoot : classes.initialRoot;
    };

    const showManualToggle = allowManualToggle || googleMapsUnavailable;

    return (
        <Controller
            name={"address" as Path<T>}
            control={control}
            render={({ field: { onChange, onBlur, value } }) => {
                // Seed the overlay from the current form state so reopening "Edit address" shows the
                // address we already have. getValues() carries the structured fields on hosts that break
                // them out (e.g. org settings); the composed `value` is parsed as a fallback otherwise.
                const parsedInitialValues = locationToManualAddressValues({
                    ...(typeof getValues === "function" ? getValues() : {}),
                    fullAddress: value,
                });
                // When the displayed address still equals the last manual entry, prefer those exact values
                // (they retain County / Address Line 2, which the composed string drops). If the address was
                // since changed via Google or typing, fall back to the parsed values.
                const manualStillCurrent =
                    Object.keys(manualValues).length > 0 && composeFullAddress({ ...defaultManualAddressValues, ...manualValues }) === (value || "");
                const manualInitialValues = manualStillCurrent ? { ...parsedInitialValues, ...manualValues } : parsedInitialValues;

                const handleManualSave = (values: IManualAddressFormValues): void => {
                    setManualValues(values);
                    const synthesizedPlace = manualAddressToPlaceAddress(values, country);
                    onChange(synthesizedPlace.formatted_address);
                    if (index !== undefined) {
                        setAddressValuesFromPlacesApi(synthesizedPlace, index);
                    } else {
                        setAddressValuesFromPlacesApi(synthesizedPlace);
                    }
                };

                return (
                    <Box sx={{ width: "100%" }}>
                        <TooltipWrapper
                            title={
                                tooltipText ?? (
                                    <FormattedMessage
                                        id="google-autocomplete-address.tooltip.text"
                                        defaultMessage="If you are looking for an Apartment number or Suite, continue typing your address past the street number & name - do not click the location and try to add additional text afterward."
                                    />
                                )
                            }
                        >
                            <StyledTextField
                                id="google-auto-compelete"
                                inputRef={placeRef}
                                variant={fieldVariant}
                                margin="normal"
                                color="primary"
                                required={!!required}
                                fullWidth
                                disabled={googleMapsUnavailable}
                                placeholder={
                                    fieldLabel ??
                                    formatMessage({
                                        id: "profile.contact-points.placeholder.address-address-line1",
                                        defaultMessage: "Search and select locations from Google",
                                    })
                                }
                                label={
                                    fieldLabel ? (
                                        <FieldLabelWithCompletion label={fieldLabel} showCompletionInfo={!!requiredToComplete} />
                                    ) : (
                                        formatMessage({ id: "common.address.label.line-1", defaultMessage: "Address Line 1" })
                                    )
                                }
                                value={value}
                                onChange={(e) => {
                                    onAddressChange();
                                    onChange(e);
                                }}
                                onFocus={handleFocus}
                                onBlur={() => onBlur()}
                                error={!!errors.address}
                                helperText={
                                    errors.address ? (
                                        <FormattedMessage id={`${errorIdPrefix}.error.${errors.address.type}`} defaultMessage={`${errors?.address?.message}`} />
                                    ) : (
                                        " "
                                    )
                                }
                                slotProps={{ inputLabel: { shrink: document.activeElement === placeRef || value !== "" } }}
                                className={showClasses()}
                            />
                        </TooltipWrapper>
                        {showManualToggle && (
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "-8px", mb: "4px" }}>
                                <Button
                                    type="button"
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    startIcon={<EditLocationAltOutlinedIcon fontSize="small" />}
                                    onClick={() => setManualOverlayOpen(true)}
                                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: "1rem" }}
                                >
                                    {value ? (
                                        <FormattedMessage id="manual-address.action.label.edit" defaultMessage="Edit address" />
                                    ) : (
                                        <FormattedMessage id="manual-address.action.label.enter" defaultMessage="Enter address manually" />
                                    )}
                                </Button>
                            </Box>
                        )}
                        <ManualAddressOverlay
                            open={manualOverlayOpen}
                            variant={overlayVariant}
                            initialValues={manualInitialValues}
                            onSave={handleManualSave}
                            onClose={() => setManualOverlayOpen(false)}
                        />
                    </Box>
                );
            }}
        />
    );
};

export default GoogleAutocompleteAddress;
